import { useState, type KeyboardEvent } from "react";
import { useLogin } from "../hooks/useLogin";

/* ── SVG Icons ─────────────────────────────────────────── */

const CotagroLogo = () => (
  <svg className="login-brand-svg" viewBox="0 0 90 56" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <polygon points="2,52 88,8 70,28" fill="#5BBA47" />
    <polygon points="14,52 84,12 70,30" fill="#006837" />
  </svg>
);

const EyeIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l18 18" />
  </svg>
);

const KeyIcon = () => (
  <svg width="32" height="32" fill="none" stroke="#006837" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
  </svg>
);

const BackIcon = () => (
  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const CheckIcon = () => (
  <svg width="24" height="24" fill="none" stroke="#5BBA47" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LockIcon = () => (
  <svg width="32" height="32" fill="none" stroke="#006837" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const SpinnerIcon = () => (
  <svg className="login-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="3" />
    <path d="M12 2a10 10 0 019.95 9" stroke="white" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

/* ── Password Input with toggle ────────────────────────── */

interface PasswordInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  id: string;
  autoComplete?: string;
  disabled?: boolean;
  autoFocus?: boolean;
}

function PasswordInput({ value, onChange, onKeyDown, placeholder, id, autoComplete, disabled, autoFocus }: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="login-password-wrapper">
      <input
        type={showPassword ? "text" : "password"}
        className="login-input"
        placeholder={placeholder}
        id={id}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoFocus={autoFocus}
      />
      <button
        type="button"
        className="login-eye-toggle"
        onClick={() => setShowPassword(!showPassword)}
        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        tabIndex={-1}
      >
        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

/* ── Recover Password Modal ────────────────────────────── */

interface RecoverModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function RecoverModal({ isOpen, onClose }: RecoverModalProps) {
  const [recoverInput, setRecoverInput] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = () => {
    if (!recoverInput.trim()) return;
    setSent(true);
    setTimeout(() => {
      setSent(false);
      handleClose();
    }, 3000);
  };

  const handleClose = () => {
    setRecoverInput("");
    setSent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="sheet-modal full-screen show" onClick={handleClose}>
      <div className="sheet-content" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <button className="sheet-back" onClick={handleClose} aria-label="Volver">
            <BackIcon />
          </button>
          <div className="sheet-title">Recuperar contraseña</div>
          <div style={{ width: 32 }} />
        </div>
        <div className="recover-content">
          <div className="recover-icon-wrap">
            <KeyIcon />
          </div>
          <div className="recover-title">¿Olvidaste tu contraseña?</div>
          <div className="recover-text">
            Ingresá tu usuario o CUIT y te enviaremos las instrucciones para recuperar tu acceso.
          </div>
          <div className="login-field">
            <input
              type="text"
              className="login-input"
              placeholder="Usuario o CUIT"
              value={recoverInput}
              onChange={(e) => setRecoverInput(e.target.value)}
              onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && handleSubmit()}
              autoFocus
            />
          </div>
          <button
            className="login-btn"
            style={{ marginTop: 8 }}
            onClick={handleSubmit}
            disabled={!recoverInput.trim()}
          >
            Enviar instrucciones
          </button>
          <div style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#757575" }}>
            Si tenés inconvenientes, contactá a tu operador comercial al{" "}
            <strong>+54 358 464-6651</strong>
          </div>
        </div>
      </div>

      {sent && (
        <div className="login-toast show">
          <CheckIcon />
          <span>Te enviamos las instrucciones por email</span>
        </div>
      )}
    </div>
  );
}

/* ── Error Banner ──────────────────────────────────────── */

function ErrorBanner({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="login-error" role="alert">
      <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
        />
      </svg>
      <span>{message}</span>
    </div>
  );
}

/* ── Login Page ────────────────────────────────────────── */

export function LoginPage() {
  const {
    username,
    password,
    newPassword,
    confirmPassword,
    error,
    isLoading,
    step,
    setUsername,
    setPassword,
    setNewPassword,
    setConfirmPassword,
    handleLogin,
    handleNewPassword,
  } = useLogin();

  const [showRecover, setShowRecover] = useState(false);

  const onLoginKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleLogin();
  };

  const onNewPassKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isLoading) handleNewPassword();
  };

  /* ── Success Screen ── */
  if (step === "AUTHENTICATED") {
    return (
      <div className="login-page active">
        <div className="login-success-screen">
          <div className="login-success-check">
            <svg width="48" height="48" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="login-success-title">¡Bienvenido!</h2>
          <p className="login-success-subtitle">Sesión iniciada correctamente</p>
        </div>
      </div>
    );
  }

  /* ── New Password Required Screen ── */
  if (step === "NEW_PASSWORD") {
    return (
      <div className="login-page active" id="page-new-password">
        <div className="login-brand">
          <CotagroLogo />
        </div>

        <div className="recover-icon-wrap" style={{ marginBottom: 20 }}>
          <LockIcon />
        </div>

        <h1 className="login-title" style={{ fontSize: 26 }}>Creá tu nueva contraseña</h1>
        <p className="login-subtitle">
          Por seguridad, necesitás cambiar tu contraseña la primera vez que ingresás.
        </p>

        <ErrorBanner message={error} />

        <div className="login-field">
          <PasswordInput
            value={newPassword}
            onChange={setNewPassword}
            onKeyDown={onNewPassKeyDown}
            placeholder="Nueva contraseña"
            id="newPass"
            autoComplete="new-password"
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="login-field">
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            onKeyDown={onNewPassKeyDown}
            placeholder="Confirmar contraseña"
            id="confirmPass"
            autoComplete="new-password"
            disabled={isLoading}
          />
        </div>

        <div style={{ fontSize: 12, color: "#9E9E9E", marginBottom: 24, paddingLeft: 4 }}>
          Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número.
        </div>

        <button className="login-btn" onClick={handleNewPassword} disabled={isLoading} id="newPassBtn">
          {isLoading ? (
            <span className="login-btn-loading">
              <SpinnerIcon />
              Guardando...
            </span>
          ) : (
            "Confirmar contraseña"
          )}
        </button>

        <div className="login-footer">Cotagro · Cooperativa Agropecuaria Limitada</div>
      </div>
    );
  }

  /* ── Login Screen ── */
  return (
    <>
      <div className="login-page active" id="page-login">
        <div className="login-brand">
          <CotagroLogo />
        </div>

        <h1 className="login-title">Bienvenido</h1>
        <p className="login-subtitle">Ingresá a tu cuenta para continuar</p>

        <ErrorBanner message={error} />

        <div className="login-field">
          <input
            type="text"
            className="login-input"
            placeholder="Usuario"
            id="loginUser"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={onLoginKeyDown}
            disabled={isLoading}
            autoFocus
          />
        </div>

        <div className="login-field">
          <PasswordInput
            value={password}
            onChange={setPassword}
            onKeyDown={onLoginKeyDown}
            placeholder="Contraseña"
            id="loginPass"
            autoComplete="current-password"
            disabled={isLoading}
          />
        </div>

        <a className="login-forgot" onClick={() => setShowRecover(true)}>
          ¿Olvidaste tu contraseña?
        </a>

        <button className="login-btn" onClick={handleLogin} disabled={isLoading} id="loginBtn">
          {isLoading ? (
            <span className="login-btn-loading">
              <SpinnerIcon />
              Ingresando...
            </span>
          ) : (
            "Ingresar"
          )}
        </button>

        <div className="login-footer">Cotagro · Cooperativa Agropecuaria Limitada</div>
      </div>

      <RecoverModal isOpen={showRecover} onClose={() => setShowRecover(false)} />
    </>
  );
}
